import { addDays, format, parseISO } from "date-fns";

export interface DateCriteriaRange {
  start?: string;
  end?: string;
}

/** Décalages proposés pour fin à partir du début (ou début à partir de la fin). */
export const INTERVAL_DAY_OFFSET_PRESETS = [3, 30, 90, 120, 180, 365] as const;

const YYYY_MM_DD = /^\d{4}-\d{2}-\d{2}$/;

const DATE_OPERATORS_NO_VALUE = ["isnull", "isnotnull"];

/** Colonne critère de type date (U2000TableColumn.type). */
export function isCriteriaDateColumn(field: { type?: string } | null | undefined): boolean {
  return field?.type === "date";
}

export function isIntervalDateOperator(name: string | undefined): boolean {
  return name?.toLowerCase() === "interval";
}

/** Opérateurs date avec saisie d'une date unique (equals, greaterthan, …). */
export function isDateSingleValueOperator(name: string | undefined): boolean {
  if (!name) {
    return false;
  }
  const op = name.toLowerCase();
  if (DATE_OPERATORS_NO_VALUE.includes(op)) {
    return false;
  }
  if (isIntervalDateOperator(name)) {
    return false;
  }
  return true;
}

export function isValidYyyyMmDd(value: string): boolean {
  if (!YYYY_MM_DD.test(value.trim())) {
    return false;
  }
  const d = new Date(`${value.trim()}T00:00:00`);
  return !Number.isNaN(d.getTime());
}

/** Garde uniquement les chiffres (saisie N jours sans décimales — préréglages intervalle). */
export function filterDigitsOnly(value: string): string {
  return String(value ?? "").replace(/\D/g, "");
}

export function parsePositiveDays(value: string | null | undefined): number | null {
  if (value == null || String(value).trim() === "") {
    return null;
  }
  const n = parseInt(String(value).trim(), 10);
  if (Number.isNaN(n) || n < 1) {
    return null;
  }
  return n;
}

export function parseIntervalValue(value: string): DateCriteriaRange {
  const parts = String(value ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (parts.length === 0) {
    return {};
  }
  if (parts.length === 1) {
    return isValidYyyyMmDd(parts[0]) ? { start: parts[0] } : {};
  }
  const start = isValidYyyyMmDd(parts[0]) ? parts[0] : undefined;
  const end = isValidYyyyMmDd(parts[1]) ? parts[1] : undefined;
  return { start, end };
}

export function serializeIntervalValue(range: DateCriteriaRange): string {
  const { start, end } = range;
  if (start && end) {
    return `${start},${end}`;
  }
  if (start) {
    return start;
  }
  return "";
}

export function isIntervalRangeComplete(range: DateCriteriaRange): boolean {
  return Boolean(range.start && range.end && isValidYyyyMmDd(range.start) && isValidYyyyMmDd(range.end));
}

/** Début et fin tombent dans le même mois civil (YYYY-MM). */
export function areIntervalBoundsSameMonth(start?: string, end?: string): boolean {
  if (!start || !end || !isValidYyyyMmDd(start) || !isValidYyyyMmDd(end)) {
    return false;
  }
  return start.slice(0, 7) === end.slice(0, 7);
}

/** Renseigne début et/ou fin manquants avec la date du jour (dialogue intervalle). */
export function withIntervalPanelDefaults(range: DateCriteriaRange, todayYmd: string): DateCriteriaRange {
  if (!isValidYyyyMmDd(todayYmd)) {
    return range;
  }
  return {
    start: range.start && isValidYyyyMmDd(range.start) ? range.start : todayYmd,
    end: range.end && isValidYyyyMmDd(range.end) ? range.end : todayYmd,
  };
}

export function formatIntervalDisplayValue(range: DateCriteriaRange): string {
  const { start, end } = range;
  if (start && end) {
    return `${start} — ${end}`;
  }
  if (start) {
    return start;
  }
  return "";
}

/** Garantit début ≤ fin lorsque les deux bornes sont définies. */
export function normalizeIntervalRangeOrder(range: DateCriteriaRange): DateCriteriaRange {
  const { start, end } = range;
  if (start && end && isValidYyyyMmDd(start) && isValidYyyyMmDd(end) && start > end) {
    return { start: end, end: start };
  }
  return range;
}

/** Applique un clic ou une date saisie sur une plage (1er = début, 2e = fin, 3e = redémarre). */
export function applyRangeDatePick(current: DateCriteriaRange, ymd: string): DateCriteriaRange {
  if (!isValidYyyyMmDd(ymd)) {
    return current;
  }
  const { start, end } = current;
  if (!start || (start && end)) {
    return { start: ymd, end: undefined };
  }
  if (ymd < start) {
    return { start: ymd, end: start };
  }
  return { start, end: ymd };
}

export function isDayInRange(ymd: string, range: DateCriteriaRange): boolean {
  const { start, end } = range;
  if (!start) {
    return false;
  }
  if (!end) {
    return ymd === start;
  }
  const [a, b] = start <= end ? [start, end] : [end, start];
  return ymd >= a && ymd <= b;
}

export function isDayRangeEndpoint(ymd: string, range: DateCriteriaRange): boolean {
  return ymd === range.start || ymd === range.end;
}

/** Ajoute `dayOffset` jours à une date `yyyy-MM-dd`. */
export function shiftYyyyMmDd(ymd: string, dayOffset: number): string | null {
  if (!isValidYyyyMmDd(ymd)) {
    return null;
  }
  const parsed = parseISO(`${ymd.trim()}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return format(addDays(parsed, dayOffset), "yyyy-MM-dd");
}

/** Fin = début + N jours (borne de début inchangée). */
export function computeIntervalEndFromStartOffset(start: string, offsetDays: number): DateCriteriaRange | null {
  const end = shiftYyyyMmDd(start, offsetDays);
  if (!end) {
    return null;
  }
  return { start, end };
}

/** Début = fin − N jours (borne de fin inchangée). */
export function computeIntervalStartFromEndOffset(end: string, offsetDays: number): DateCriteriaRange | null {
  const start = shiftYyyyMmDd(end, -offsetDays);
  if (!start) {
    return null;
  }
  return { start, end };
}
